/**
 * Add / edit an item.
 *
 * PRICES ARE TYPED IN RUPEES AND STORED IN PAISE
 * ----------------------------------------------
 * The model stores integer paise; a shopkeeper types `120.50`. The conversion is
 * `parseMoney` at submit and nothing else — never `* 100` written inline, which
 * is where `119.99` becomes `11998`. Blank means "leave it unset", NOT zero: an
 * item with no selling price yet is normal, and writing a zero would put a free
 * item in the catalogue that later raises a line total of nothing.
 *
 * Validators come from `masterDataRules` — the same functions `productService`
 * calls — so the form cannot be more forgiving than the save it guards.
 */
import { useMemo, useState } from 'react';
import { Drawer } from '@ui/components/Drawer';
import { Form } from '@ui/components/Form';
import { Button } from '@ui/components/Button';
import { Field, Input } from '@ui/components/Field';
import { parseMoney, asMoney } from '@core/money/types';
import { formatAmount, formatRateBp } from '@core/money/format';
import { collectProblems } from '@ui/lib/formValidation';
import {
  hsnField,
  priceField,
  productNameField,
  skuField,
} from '@features/masterData/masterDataRules';
import { STANDARD_UNITS } from '@features/masterData/units';
import { useCreateProduct, useUpdateProduct } from './useProductWrites';
import type { Product, TaxRate } from '@data/db/types';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Null means CREATE; a row means EDIT. */
  readonly product: Product | null;
  /** Available tax rates for this business, for the GST dropdown. */
  readonly taxRates: readonly TaxRate[];
}

interface FormState {
  name: string;
  sku: string;
  hsnSac: string;
  unit: string;
  unitPrecision: number;
  salePrice: string;
  purchasePrice: string;
  kind: 'GOODS' | 'SERVICE';
  isStockTracked: boolean;
  isActive: boolean;
  taxRateId: string;
}

const BLANK: FormState = {
  name: '',
  sku: '',
  hsnSac: '',
  unit: 'NOS',
  unitPrecision: 0,
  salePrice: '',
  purchasePrice: '',
  kind: 'GOODS',
  isStockTracked: true,
  isActive: true,
  taxRateId: '',
};

/**
 * Paise to the rupees string a text field shows.
 *
 * `formatAmount`, NOT `formatMoney`: this string goes back INTO an `<input>`, and
 * `formatMoney` prefixes "₹", which would then be re-parsed by `parseMoney` on the
 * next save. It also goes through the core formatter rather than a local
 * `toFixed`, because every other price on screen is formatted the same way and two
 * views of one number must not disagree about decimals.
 */
const toRupees = (paise: number | undefined): string =>
  // The grouping comma is stripped: `formatAmount` is built for a table cell, but
  // this value goes back into an `<input>` and is re-parsed by `parseMoney` on the
  // next save, which reads a comma as anything but a digit.
  paise === undefined ? '' : formatAmount(asMoney(paise)).replace(/,/g, '');

/**
 * Find the default tax rate ID from available tax rates.
 * Prefers 18% (1800 bp) as default, falls back to first available rate.
 */
function getDefaultTaxRateId(taxRates: readonly TaxRate[]): string {
  const defaultRate = taxRates.find((t) => t.rateBp === 1800 && t.isActive);
  if (defaultRate) return defaultRate.id;
  // Fallback to first active tax rate
  const firstActive = taxRates.find((t) => t.isActive);
  return firstActive?.id ?? '';
}

/**
 * Format rateBp to percentage string for display (e.g., 1800 -> "18%").
 */
function formatTaxRateForDisplay(rateBp: number): string {
  return `${formatRateBp(rateBp)}%`;
}

export function ProductEditorDrawer({ open, onClose, product, taxRates }: Props) {
  const [state, setState] = useState<FormState>(BLANK);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  /** A refusal from the service, as words. Null when the last save was fine. */
  const [saveError, setSaveError] = useState<string | null>(null);
  const create = useCreateProduct({ onDone: onClose });
  const update = useUpdateProduct({ onDone: onClose });

  const targetId = product?.id ?? 'new';
  /**
   * SEEDING DURING RENDER, GUARDED — the pattern `ReturnEditorScreen` and the
   * Delivery Note editor already use, for the same two reasons: an effect re-runs
   * on every refetch and would overwrite what the user had just typed, and
   * `setState` inside an effect body causes a cascading render. The guard seeds
   * exactly once per opened item, so a re-render mid-edit changes nothing.
   */
  if (open && loadedId !== targetId) {
    setState(
      product
        ? {
            name: product.name,
            sku: product.sku,
            hsnSac: product.hsnSac ?? '',
            unit: product.unit,
            unitPrecision: product.unitPrecision ?? 0,
            salePrice: toRupees(product.salePricePaise),
            purchasePrice: toRupees(product.purchasePricePaise),
            kind: product.kind,
            isStockTracked: product.isStockTracked,
            isActive: product.isActive,
            taxRateId: product.taxRateId ?? getDefaultTaxRateId(taxRates),
          }
        : {
            ...BLANK,
            taxRateId: getDefaultTaxRateId(taxRates),
          },
    );
    setLoadedId(targetId);
    setSaveError(null);
  }

  const problems = useMemo(
    () =>
      collectProblems({
        name: productNameField(state.name),
        sku: skuField(state.sku),
        hsn: hsnField(state.hsnSac),
        salePrice:
          state.salePrice.trim() === ''
            ? { ok: true }
            : priceField(state.salePrice, 'Selling price'),
        purchasePrice:
          state.purchasePrice.trim() === ''
            ? { ok: true }
            : priceField(state.purchasePrice, 'Purchase price'),
      }),
    [state],
  );

  const busy = create.isPending || update.isPending;
  const ready = Object.keys(problems).length === 0;

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((old) => ({ ...old, [key]: value }));
  }

  async function submit() {
    // A service refusal (a duplicate item code, a row deleted elsewhere) must be
    // SHOWN, not swallowed — an unhandled rejection leaves the form sitting there,
    // which reads to a user as a broken app rather than as a rule they can act on.
    try {
      // Blank price means unset. `undefined` and `0` are different facts and the
      // document engine treats them differently. `parseMoney` returns branded PAISE,
      // so this is where rupees typed by a person become the integer the row stores.
      const toPaise = (text: string): number | undefined =>
        text.trim() === '' ? undefined : parseMoney(text);
      const sale = toPaise(state.salePrice);
      const purchase = toPaise(state.purchasePrice);

      const payload = {
        name: state.name.trim(),
        sku: state.sku.trim(),
        ...(state.hsnSac.trim() ? { hsnSac: state.hsnSac.trim() } : {}),
        unit: state.unit.trim() || 'NOS',
        unitPrecision: state.unitPrecision,
        kind: state.kind,
        isStockTracked: state.isStockTracked,
        isActive: state.isActive,
        ...(state.taxRateId ? { taxRateId: state.taxRateId } : {}),
        ...(sale !== undefined ? { salePricePaise: sale } : {}),
        ...(purchase !== undefined ? { purchasePricePaise: purchase } : {}),
      };
      if (product) {
        await update.mutateAsync({ id: product.id, input: payload });
      } else {
        await create.mutateAsync(payload);
      }
    } catch (error) {
      // Keep the form open and holding what was typed, so one field can be fixed
      // and Save pressed again rather than the whole row retyped.
      setSaveError(error instanceof Error ? error.message : 'Could not save this item.');
    }
  }

  const title = product ? `Edit ${product.name}` : 'New item';

  return (
    <Drawer open={open} onClose={onClose} title={title} locked={busy}>
      <Form
        aria-label={title}
        busy={busy}
        onSubmit={() => void submit()}
        footer={
          <>
            <Button type="submit" disabled={!ready || busy} className="flex-1">
              {product ? 'Save changes' : 'Create item'}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
          </>
        }
      >
        {saveError && (
          <p
            role="alert"
            data-testid="product-save-error"
            className="rounded border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-200"
          >
            {saveError}
          </p>
        )}
        <Field label="Item name" required error={problems.name}>
          {(f) => (
            <Input
              {...f}
              data-testid="product-name"
              value={state.name}
              invalid={Boolean(problems.name)}
              onChange={(e) => set('name', e.target.value)}
            />
          )}
        </Field>
        <Field
          label="Item code / SKU"
          hint="Optional, but unique when set."
          error={problems.sku}
        >
          {(f) => (
            <Input
              {...f}
              data-testid="product-sku"
              value={state.sku}
              invalid={Boolean(problems.sku)}
              onChange={(e) => set('sku', e.target.value)}
            />
          )}
        </Field>
        <Field label="HSN / SAC" hint="4, 6 or 8 digits. Blank is allowed." error={problems.hsn}>
          {(f) => (
            <Input
              {...f}
              data-testid="product-hsn"
              value={state.hsnSac}
              invalid={Boolean(problems.hsn)}
              onChange={(e) => set('hsnSac', e.target.value)}
            />
          )}
        </Field>
        <Field label="Unit" hint="Select the unit of measure">
          {(f) => (
            <select
              {...f}
              data-testid="product-unit"
              value={state.unit}
              onChange={(e) => set('unit', e.target.value)}
              className="rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
            >
              {STANDARD_UNITS.map((u) => (
                <option key={u.code} value={u.code}>
                  {u.label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field
          label="Decimal places"
          hint="Decimals allowed for this unit (e.g., 3 for KG, 2 for SQFT, 0 for NOS)"
        >
          {(f) => (
            <select
              {...f}
              data-testid="product-unit-precision"
              value={state.unitPrecision}
              onChange={(e) => set('unitPrecision', Number(e.target.value))}
              className="rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
            >
              {[0, 1, 2, 3].map((p) => (
                <option key={p} value={p}>
                  {p} decimal place{p === 1 ? '' : 's'}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="GST Rate" hint="Select the applicable GST rate">
          {(f) => (
            <select
              {...f}
              data-testid="product-gst-rate"
              value={state.taxRateId}
              onChange={(e) => set('taxRateId', e.target.value)}
              className="rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
            >
              <option value="">Select GST rate</option>
              {taxRates
                .filter((t) => t.isActive)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {formatTaxRateForDisplay(t.rateBp)}
                  </option>
                ))}
            </select>
          )}
        </Field>
        <Field
          label="Selling price"
          hint="Per unit, in rupees. Blank leaves it unset."
          error={problems.salePrice}
        >
          {(f) => (
            <Input
              {...f}
              data-testid="product-sale-price"
              inputMode="decimal"
              value={state.salePrice}
              invalid={Boolean(problems.salePrice)}
              onChange={(e) => set('salePrice', e.target.value)}
            />
          )}
        </Field>
        <Field
          label="Purchase price"
          hint="What you pay for it. Blank leaves it unset."
          error={problems.purchasePrice}
        >
          {(f) => (
            <Input
              {...f}
              data-testid="product-purchase-price"
              inputMode="decimal"
              value={state.purchasePrice}
              invalid={Boolean(problems.purchasePrice)}
              onChange={(e) => set('purchasePrice', e.target.value)}
            />
          )}
        </Field>
        <Field label="Type">
          {(f) => (
            <select
              {...f}
              data-testid="product-kind"
              value={state.kind}
              className="rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
              onChange={(e) => set('kind', e.target.value as 'GOODS' | 'SERVICE')}
            >
              <option value="GOODS">Goods — kept in stock</option>
              <option value="SERVICE">Service — not stocked</option>
            </select>
          )}
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            data-testid="product-stock-tracked"
            checked={state.isStockTracked}
            onChange={(e) => set('isStockTracked', e.target.checked)}
          />
          Keep stock for this item
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            data-testid="product-active"
            checked={state.isActive}
            onChange={(e) => set('isActive', e.target.checked)}
          />
          Available to use on new documents
        </label>
      </Form>
    </Drawer>
  );
}